import { Observable } from "rxjs/Observable"
import {
    NOTIFICATION_LIST_REQUEST,
    NOTIFICATION_LIST_SEARCH_REQUEST,
    NOTIFICATION_APPLY_FILTER_CRITERIA,
    NOTIFICATION_ITEM_REQUEST,
    NOTIFICATION_LIST_RESPONSE,
    NOTIFICATION_ITEM_RESPONSE,
    NOTIFICATION_ITEM_CACHED_RESPONSE,
    NOTIFICATION_CANCEL_MODEL_UPDATE,
    NOTIFICATION_ITEM_SAVE_REQUEST,
    NOTIFICATION_ITEM_SAVE_RESPONSE,
    NOTIFICATION_APPLY_MODEL_UPDATE,
    NOTIFICATION_ITEM_DELETE_REQUEST,
    NOTIFICATION_ITEM_DELETE_RESPONSE,
    NOTIFICATION_ITEM_ENTITY_REQUEST,
} from "../actions/action-types"
import {
    notificationListCachedResponse,
    notificationListResponse,
    notificationListErrorResponse,
    notificationListRequest,
    notificationItemResponse,
    notificationItemCachedResponse,
    notificationItemErrorResponse,
    notificationItemRequest,
    notificationItemSaveRequest,
    notificationItemSaveResponse,
    notificationListInvalidationRequest,
    notificationItemDeleteResponse,
    notificationItemDeleteErrorResponse,
    notificationItemEntityResponse,
    notificationItemEntityErrorResponse,
} from "../actions/notification-actions"
import { defaultSearchCriteria } from "../reducers/paging/notification-paging-reducer"
import {
    hydrate as hydrateNotification,
    normalisingFields,
} from "../models/entities/notification"
import {
    getNotificationList,
    getNotificationItem,
    patchNotificationItem,
    postNotificationItem,
    deleteNotificationItem,
} from "../api/notification-api"
import {
    requestListEpic,
    requestListSearchEpic,
    requestListFilterEpic,
    requestItemEpic,
    requestItemSaveEpic,
    requestItemDeleteEpic,
    requestTopItemFromListResponseEpic,
    syncIdToURLEpic,
} from "./default-epics/default-entity-epics"
import { getKnowledgeItem } from "../api/knowledge-api"
import { getTopicItem } from "../api/topic-api"
import { getClusterItem } from "../api/cluster-api"
import { getNotificationFutureDate } from "../components/notifications/util"
import moment from "moment"

export const requestNotificationListEpic = requestListEpic(
    NOTIFICATION_LIST_REQUEST,
    getNotificationList,
    "notifications",
    notificationListResponse,
    notificationListCachedResponse,
    notificationListErrorResponse
)

export const requestNotificationSearchEpic = requestListSearchEpic(
    NOTIFICATION_LIST_SEARCH_REQUEST,
    notificationListRequest,
    "notifications"
)

export const applyNotificationFilterCriteria = requestListFilterEpic(
    NOTIFICATION_APPLY_FILTER_CRITERIA,
    notificationListRequest,
    "notifications"
)

export const requestNotificationItemEpic = requestItemEpic(
    NOTIFICATION_ITEM_REQUEST,
    getNotificationItem,
    "notifications",
    hydrateNotification,
    normalisingFields,
    notificationItemResponse,
    notificationItemCachedResponse,
    notificationItemErrorResponse
)

export const requestNotificationItemOnSave = (action$, { getState }) =>
    action$
        .ofType(NOTIFICATION_ITEM_SAVE_RESPONSE)
        .map(() => getState().presentation.notifications.activeId)
        .filter((id) => id)
        .mergeMap((id) => {
            const { paging, presentation } = getState()
            const pagingRequest =
                paging.notifications[presentation.notifications.searchCriteria]
            return Observable.of(
                notificationItemRequest(id, true),
                notificationListInvalidationRequest(pagingRequest)
            )
        })

export const requestNotificationCancelModelUpdateEpic = (
    action$,
    { getState }
) =>
    action$
        .ofType(NOTIFICATION_CANCEL_MODEL_UPDATE)
        .map(() => getState().presentation.notifications.activeId)
        .filter((id) => id)
        .map(notificationItemRequest)

export const requestNotificationApplyModelUpdateEpic = (
    action$,
    { getState }
) =>
    action$.ofType(NOTIFICATION_APPLY_MODEL_UPDATE).map(() => {
        const { activeId, updateModel } = getState().presentation.notifications
        let newUpdateModel = activeId
            ? updateModel.set("id", activeId)
            : updateModel
        if (!activeId) {
            const futureDate = getNotificationFutureDate()
            const notBeforeDate = moment.unix(newUpdateModel.get("not_before"))
            if (futureDate.isAfter(notBeforeDate)) {
                newUpdateModel = newUpdateModel.set(
                    "not_before",
                    futureDate.unix()
                )
            }
            if (newUpdateModel.get("expires")) {
                const oneHourAfter = moment
                    .unix(newUpdateModel.get("not_before"))
                    .add(1, "hours")
                if (
                    oneHourAfter.isAfter(
                        moment.unix(newUpdateModel.get("expires"))
                    )
                ) {
                    newUpdateModel = newUpdateModel.set(
                        "expires",
                        oneHourAfter.unix()
                    )
                }
            }
        }
        return notificationItemSaveRequest(
            activeId,
            newUpdateModel.toJS(),
            undefined
        )
    })

export const requestNotificationItemSaveEpic = requestItemSaveEpic(
    NOTIFICATION_ITEM_SAVE_REQUEST,
    patchNotificationItem,
    postNotificationItem,
    null,
    notificationItemSaveResponse,
    notificationItemErrorResponse
)

export const responseNotificationListEpic = requestTopItemFromListResponseEpic(
    NOTIFICATION_LIST_RESPONSE,
    notificationItemRequest,
    "notifications",
    "notifications",
    defaultSearchCriteria
)

export const syncNotificationIdToURLEpic = syncIdToURLEpic(
    "notifications",
    NOTIFICATION_ITEM_RESPONSE,
    NOTIFICATION_ITEM_SAVE_RESPONSE,
    NOTIFICATION_ITEM_CACHED_RESPONSE
)

export const requestNotificationItemDeleteEpic = requestItemDeleteEpic(
    NOTIFICATION_ITEM_DELETE_REQUEST,
    deleteNotificationItem,
    "notification",
    notificationItemDeleteResponse,
    notificationItemDeleteErrorResponse
)

export const responseNotificationItemDeleteEpic = (action$, { getState }) =>
    action$.ofType(NOTIFICATION_ITEM_DELETE_RESPONSE).map((action) => {
        const state = getState()
        const string = state.presentation.notifications.searchCriteria
        const pagingRequest = state.paging.notifications[string]
        return notificationItemRequest(pagingRequest.data[0])
    })

export const requestNotificationEntityEpic = (action$, { getState }) =>
    action$.ofType(NOTIFICATION_ITEM_ENTITY_REQUEST).mergeMap((action) => {
        const { token } = getState().authentication
        const getItemAPI = getNotificationEntityFetchFunction(
            action.notificationType
        )
        if (!getItemAPI) {
            const errorAction = notificationItemEntityErrorResponse(
                new Error("Invalid notification type")
            )
            return Observable.of(errorAction)
        }
        return getItemAPI(token, action.entityId)
            .map((data) => notificationItemEntityResponse(data))
            .catch((error) =>
                notificationItemEntityErrorResponse(error, action.entityId)
            )
    })

const getNotificationEntityFetchFunction = (notificationType) => {
    switch (notificationType) {
        case "knowledge":
            return getKnowledgeItem
        case "topic":
            return getTopicItem
        case "cluster":
            return getClusterItem
        default:
            return null
    }
}
